import React, { useState, useEffect } from 'react';

/**
 * FallbackImage Component
 * @description Renders an image, trying each source URL in turn and falling back to
 * the next if one fails to load. If every source fails (or there are none), it shows
 * a clean branded initial-letter avatar instead of a broken-image icon or "No Logo".
 * It receives all data via props and does not load files directly.
 * @param {string[]|string} srcs - An array of image URLs to try, or a single URL string.
 * @param {string} alt - The alt text for the image (e.g. "Macy's logo").
 * @param {string} className - Tailwind CSS classes for styling.
 * @param {string} [name] - Optional explicit brand name for the placeholder initial.
 */

// A small palette of pleasant, accessible background tints for the placeholder.
const PLACEHOLDER_COLORS = [
    'bg-rose-100 text-rose-700', 'bg-amber-100 text-amber-700', 'bg-emerald-100 text-emerald-700',
    'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700', 'bg-teal-100 text-teal-700',
    'bg-orange-100 text-orange-700', 'bg-indigo-100 text-indigo-700',
];

function colorFor(label) {
    let hash = 0;
    for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
    return PLACEHOLDER_COLORS[hash % PLACEHOLDER_COLORS.length];
}

export default function FallbackImage({ srcs, alt, className, name }) {
    // Ensure that srcs is always an array, filtering out empty/null entries.
    const sources = (Array.isArray(srcs) ? srcs : [srcs]).filter(Boolean);

    const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
    const [error, setError] = useState(false);

    // Stable string of the sources so the effect only re-runs when they change.
    const sourcesString = sources.join(',');

    useEffect(() => {
        setCurrentSrcIndex(0);
        setError(false);
    }, [sourcesString]);

    const handleError = () => {
        // Advance to the next source; if none remain, show the placeholder.
        if (currentSrcIndex < sources.length - 1) {
            setCurrentSrcIndex(currentSrcIndex + 1);
        } else {
            setError(true);
        }
    };

    // No usable sources (or all failed): render an initial-letter avatar.
    if (error || sources.length === 0) {
        const label = (name || (alt || '').replace(/\s*logo\s*$/i, '')).trim();
        const initial = label ? label[0].toUpperCase() : '?';
        return (
            <div
                className={`${className} flex items-center justify-center rounded-md font-bold ${colorFor(label || '?')}`}
                aria-label={alt}
                title={label}
            >
                <span className="text-xl leading-none">{initial}</span>
            </div>
        );
    }

    return (
        <img
            src={sources[currentSrcIndex]}
            alt={alt}
            className={className}
            onError={handleError}
        />
    );
}
